<template>
  <div id="global-search" class="ui simple dimmer" style="z-index: 1000;" @keydown.esc="hide">
    <div class="ui inverted transparent segment">
      <!-- Search input -->
      <div class="ui inverted transparent fluid huge icon input">
        <input
          autofocus
          type="text"
          :placeholder="$t('SEARCH')"
          v-model="searchTerm"
          @keydown.down.prevent="selectNext"
          @keydown.up.prevent="selectPrevious"
          @keydown.enter.prevent="navigateToSelection"
          autocomplete="off"
          ref="searchInput"
          @blur="hideWithDelay(300)"
        />
        <i class="search icon"></i>
      </div>

      <div class="ui inverted divider"></div>

      <!-- Results -->
      <div class="ui large relaxed inverted selection list fuzzy-list">
        <div
          class="item"
          :class="{ active: i === selectedIndex }"
          :key="result.original.id"
          @click.prevent="navigate(result.original.route)"
          v-for="(result, i) in filteredAndCappedItems"
          :ref="'entry-' + i"
        >
          <i :class="result.original.iconClass" class="middle aligned icon"></i>
          <div class="content" style="pointer-events: none;">
            <div class="header" v-html="result.string"></div>
            <div class="description">{{ result.original.description }}</div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss">
// Scss is not scoped because we use v-html
@charset "utf-8";

// Namespace with an id in stead
#global-search.dimmer .transparent.segment {
  background: transparent;
  text-align: left;
  margin-right: 20px;

  .selection.list {
    overflow-y: scroll;
    height: calc(100vh - 100px);
  }
}
</style>

<script>
// @flow
import _ from 'lodash'
import { Flow, Field, Application, Group, Table } from 'services'
import FuzzyFilterer from '../../Mixins/FuzzyFilterer'

export default {
  name: 'globalSearch',
  props: ['tempSearchTerm'],
  mixins: [FuzzyFilterer],
  data() {
    return {
      selectedIndex: 0,
      commonActionEntries: [
        {
          header: this.$i18n.t('NEW', { thing: this.$i18n.tc('APPLICATION', 1) }),
          iconClass: { star: true },
          route: { name: 'new-app' }
        },
        {
          header: this.$i18n.t('NEW', { thing: this.$i18n.tc('GROUP', 1) }),
          iconClass: { star: true },
          route: { name: 'new-group' }
        },
        {
          header: this.$i18n.t('NEW', { thing: this.$i18n.tc('USER', 1) }),
          iconClass: { star: true },
          route: { name: 'new-user' }
        },
        {
          header: this.$i18n.t('NEW', { thing: this.$i18n.tc('SUBJECT', 1) }),
          iconClass: { star: true },
          route: { name: 'new-subject' }
        },
        {
          header: this.$i18n.t('NEW', { thing: this.$i18n.tc('TABLE', 1) }),
          iconClass: { star: true },
          route: { name: 'new-table' }
        }
      ],
      hidden: false
    }
  },

  computed: {
    flows() {
      return this.$store.state.flow.items
    },
    fields() {
      return this.$store.state.field.items
    },
    apps() {
      return this.$store.state.application.items
    },
    groups() {
      return this.$store.state.group.items
    },
    tables() {
      return this.$store.state.table.items
    },
    // For filtering mixin
    items() {
      if (!this.searchTerm) return this.commonActionEntries || []
      return this.fullListOfThings || []
    },
    filteredAndCappedItems() {
      // Cap items produced by filtering mixin
      let results = this.filteredItems
      const maxResults = 50
      if (results.length > maxResults) {
        const omittedResultCount = results.length - maxResults
        results = results.slice(0, maxResults - 1).concat([
          {
            string: this.$t('TOO_MANY_MATCHES', { omitted: omittedResultCount }),
            original: {
              header: this.$t('TOO_MANY_MATCHES', { omitted: omittedResultCount }),
              iconClass: { exclamation: true, yellow: true, triangle: true }
            }
          }
        ])
      }
      this.selectedIndex = Math.min(this.selectedIndex, results.length - 1)
      return results
    },
    fullListOfThings() {
      const appLessThings = []
      // Fields
      let allFields = _.map(
        _.filter(this.fields, field => {
          if (!_.isEmpty(this.apps) && !_.find(this.apps, ['id', field.application])) {
            appLessThings.push(field)
            return false
          }
          return true
        }),
        f => {
          const fieldApp = _.find(this.apps, ['id', f.application])
          return {
            id: f.id,
            header: f.name,
            description: fieldApp ? fieldApp.name : '',
            iconClass: { bookmark: true },
            route: {
              name: 'app-field',
              params: {
                appId: f.application,
                fieldId: f.id
              }
            }
          }
        }
      )
      if (appLessThings.length > 0)
        console.warn(`${appLessThings.length} fields without Apps!`, appLessThings)

      appLessThings.length = 0
      // Flows
      let allFlows = _.map(
        _.filter(this.flows, flow => {
          if (!_.find(this.apps, ['id', flow.application])) {
            appLessThings.push(flow)
            return false
          }
          return true
        }),
        f => {
          const flowApp = _.find(this.apps, ['id', f.application])
          return {
            id: f.id,
            header: f.name,
            description: flowApp ? flowApp.name : '',
            iconClass: { random: true },
            route: {
              name: 'app-flow',
              params: {
                appId: f.application,
                flowId: f.id,
                mode: 'auto'
              }
            }
          }
        }
      )
      if (appLessThings.length > 0)
        console.warn(`${appLessThings.length} flows without Apps!`, appLessThings)

      // Apps
      let allApps = _.map(this.apps, a => {
        return {
          id: a.id,
          header: a.name,
          description: a.version,
          iconClass: { cube: true },
          route: {
            name: 'app-settings',
            params: {
              appId: a.id
            }
          }
        }
      })
      // Groups
      let allGroups = _.map(this.groups, g => {
        return {
          id: g.id,
          header: g.name,
          description: '',
          iconClass: { group: true },
          route: {
            name: 'group',
            params: {
              groupId: g.id
            }
          }
        }
      })
      // Subjects
      let allSubjects = _.map(this.subjects, s => {
        return {
          id: s.id,
          header: s.name,
          description: s.key,
          iconClass: { book: true },
          route: {
            name: 'subject',
            params: {
              subjectId: s.id
            }
          }
        }
      })
      // Tables
      let allTables = _.map(this.tables, t => {
        return {
          id: t.id,
          header: t.name,
          description: t.name,
          iconClass: { table: true },
          route: {
            name: 'table',
            params: {
              tableId: t.id,
              mode: 'data'
            }
          }
        }
      })
      return _.concat(
        allFields,
        allFlows,
        allApps,
        allGroups,
        allSubjects,
        allTables,
        this.commonActionEntries
      )
    }
  },

  methods: {
    selectNext() {
      // Rotate to first element when going beyond the last
      this.selectedIndex =
        this.selectedIndex + 1 >= this.filteredAndCappedItems.length ? 0 : this.selectedIndex + 1
      this.scrollToItemIndex()
    },

    scrollToItemIndex() {
      // Circumvent array refs not being in render order under v-for
      const itemEl = _.head(this.$refs['entry-' + this.selectedIndex] || [])
      if (itemEl)
        itemEl.scrollIntoView({
          behavior: 'smooth',
          block: 'center'
        })
    },

    selectPrevious() {
      // Rotate back to last element when going beyond 0
      this.selectedIndex =
        this.selectedIndex - 1 < 0 ? this.filteredAndCappedItems.length - 1 : this.selectedIndex - 1
      this.scrollToItemIndex()
    },

    navigate(route) {
      if (!route) return
      this.$router.push(route)
      this.hide()
    },

    navigateToSelection() {
      if (
        this.filteredAndCappedItems[this.selectedIndex] &&
        this.filteredAndCappedItems[this.selectedIndex].original.route
      ) {
        this.navigate(this.filteredAndCappedItems[this.selectedIndex].original.route)
      }
    },

    hide() {
      if (this.hidden) return
      this.hidden = true
      this.$emit('done')
    },

    hideWithDelay(delayMs) {
      if (this.hidden) return
      setTimeout(() => this.hide(), delayMs)
    },

    filterExtract(item) {
      return item.header
    },

    focusInputInitial() {
      setTimeout(() => {
        const ref = this.$refs.searchInput
        this.searchTerm = this.tempSearchTerm
        if (ref) ref.focus()
      }, 20)
    }
  },

  created() {
    Flow.readAllFlows()
    Field.readFields()
    Application.readAllApps()
    Group.readAllGroups()
    Table.readAllTables()
    this.focusInputInitial()
  }
}
</script>
